import { type AlertColor } from '@mui/material'
import { ExtendedPromise } from './promises-utils'

export function isEmptyString (value: string | null | undefined): boolean {
  return value == null || value.length === 0
}

export function isValidNumberString (value: string): boolean {
  return value?.trim().length !== 0 && !Number.isNaN(Number(value))
}

/**
 * Checks whether the provided string is a valid object
 * This will return true for valid objects and arrays, and return false for functions and null
 * @param value string to be checked
 */
export function isValidObjectString (value: string): boolean {
  return typeof value === 'object' && value !== null
}

/**
 * Checks whether the provided string is a valid object (and only object)
 * This will return true for valid objects, and return false for arrays, functions and null
 * @param value string to be checked
 */
export function isStrictlyValidObjectString (value: string): boolean {
  return isValidObjectString(value) && !Array.isArray(value)
}

export function toTitleCase (value: string): string {
  return value.replace(
    /\w\S*/g,
    function (txt: string) {
      return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
    }
  )
}

function truncate (value: string, length: number): string {
  return value.substring(0, Math.min(length, value.length)) + '...'
}

export function formAlertSuccessMessage (code: number, codeText: string, response: string): string {
  return (
    `Successfully submitted request to strategy
    
    Code: ${code} ${isEmptyString(codeText) ? '' : `(${codeText})`}
    Response: ${response}`
  )
}

export function formAlertResponseMessageWithPrompt (response: any): string {
  return formAlertResponseMessage(response, true)
}

export function formAlertResponseMessageNoPrompt (response: any): string {
  return formAlertResponseMessage(response, false)
}

function getResponseMsg (response: any): string {
  if (response.value == null && response.reason == null) {
    return ''
  }

  // Handle rejected promise
  if (response?.reason != null) {
    return response.reason
  }

  // Following a simplified version of: https://google.github.io/styleguide/jsoncstyleguide.xml
  // If the top-most "data" property has a "data" object -> successful response
  // If the top-most "data" property has an "error" object -> failed response
  const data = response.value.data

  // Check if top-most "data" property has an "error" obj representing a failed response
  const hasErrorObj = isStrictlyValidObjectString(data?.error)
  if (hasErrorObj) {
    const errorObj = data.error
    // If errorObj has "message" as one of its top-level properties, use it
    if (errorObj.message != null) {
      return errorObj.message
    }

    // If errorObj has "errors" map as one of its top-level properties, use it
    const errorObjHasErrorsMap = errorObj?.errors != null && isStrictlyValidObjectString(errorObj.errors)
    if (errorObjHasErrorsMap) {
      return truncate(JSON.stringify(errorObj.errors), 20)
    }

    // Otherwise, stringify errorObj and truncate it
    return truncate(JSON.stringify(errorObj), 20)
  }

  // Expecting top-most "data" property to have a "data" obj representing a successful response
  const hasDataObj = isStrictlyValidObjectString(data?.data)
  if (hasDataObj) {
    const dataObj = data.data
    // If dataObj has "message" as one of its top-level properties, use it
    // Otherwise, stringify dataObj and truncate it
    return dataObj.message ?? truncate(JSON.stringify(dataObj), 20)
  }

  // Handle other response formats
  // If the top-most "data" property has "message" as one of its top-level properties, use it
  // If the top-most "data" property is an object, stringify and truncate it
  // Otherwise, simply use the top-most "data" property as the message
  return data?.message ??
    (
      isStrictlyValidObjectString(data)
        ? truncate(JSON.stringify(data), 20)
        : data
    )
}

function formAlertResponseMessage (response: any, promptToReload: boolean): string {
  let responseStatus
  let responseMsg
  let responseStatusText = null

  if (ExtendedPromise.isRaceSettleAllResult(response)) {
    responseStatus = `Fulfilled: ${response.fulfilled.length} | Rejected: ${response.rejected.length}`
    responseMsg = `${response.all.map((res, index) => `[${index + 1}] ${getResponseMsg(res)}`).join(', ')}`
  } else if (Array.isArray(response)) {
    const statusArr: number[] = response.map(item => item.status)
    const statusBuckets: Record<string, number> = {}
    for (const status of statusArr) {
      const stringifiedStatus = String(status)
      if (statusBuckets[stringifiedStatus] == null) {
        statusBuckets[stringifiedStatus] = 1
      } else {
        statusBuckets[stringifiedStatus] = statusBuckets[stringifiedStatus] + 1
      }
    }
    responseStatus = Object.keys(statusBuckets)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map(status => `${status} (${statusBuckets[status]})`).join(', ')
    responseMsg = `${response.map((res, index) => `[${index + 1}] ${getResponseMsg(res)}`).join(', ')}`
  } else {
    responseStatus = response.status
    responseMsg = getResponseMsg(response)
    responseStatusText = response.statusText
  }

  return (
    `
    Code: ${responseStatus} ${isEmptyString(responseStatusText) ? '' : `(${responseStatusText})`}
    Response: ${responseMsg}` +
    (
      promptToReload
        ? '\n\n( Use the reload button to retrieve the latest state )'
        : ''
    )
  )
}

export function handleRaceSettleAllResponse (
  response: any,
  promptReload: boolean,
  toastCallback: (message: string, severity: AlertColor) => void
): void {
  if (ExtendedPromise.isRaceSettleAllResult(response)) {
    const allCount = response.all.length

    const alertMessage = promptReload
      ? formAlertResponseMessageWithPrompt(response)
      : formAlertResponseMessageNoPrompt(response)

    if (response.fulfilled.length === allCount) {
      toastCallback('Successfully processed request\n' + alertMessage, 'success')
    } else if (response.rejected.length === allCount) {
      toastCallback('Failed to process request\n' + alertMessage, 'error')
    } else {
      toastCallback('Failed to partially process request\n' + alertMessage, 'warning')
    }
  }
}

export function formAlertErrorMessage (error: any): string {
  const code = error?.response?.status ?? error?.code ?? 'Unknown Code Format (if you see this, report it to your site admin)'
  const statusText = !isEmptyString(error?.response?.statusText) ? `(${error?.response?.statusText})` : ''
  const message = error?.response?.data?.error?.message ?? error?.message ?? 'Unknown Message Format (if you see this, report it to your site admin)'

  return (
    `Failed to submit request to strategy
    
    Code: ${code} ${statusText}
    Message: ${message}`
  )
}
